/**
 * V2WorkshopBuildPanel — Phase 2: Michael-gated plugin generation.
 * Generate → validation PASS/FAIL report → file preview → download .zip.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('kindra-admin-token')}` } });

const C = { navy: '#100838', surface: '#1A1150', hair: '#2A2070', icy: '#DCF0F5',
  muted: 'rgba(220,240,245,0.62)', faint: 'rgba(220,240,245,0.42)', aqua: '#34D1B0',
  spectrum: 'linear-gradient(95deg,#34D1B0 0%,#1FB6C1 25%,#4F8FD0 50%,#9156C4 75%,#EB258D 100%)' };

// ── Money Desk (Phase A) — Michael-set pricing → Xero DRAFT invoice ────────
const TIERS = [
  { id: 'solo',       label: 'Solo / Simple',    anchor: 1500 },
  { id: 'small_team', label: 'Small team',       anchor: 2500 },
  { id: 'complex',    label: 'Larger / Complex', anchor: 4000 },
];
const ADDON_ANCHORS = { phone_answering: 1500, payment_processing: 800, bulk_sms: 600, website_livechat: 1200, extra_agent: 500, other: 0 };

const MoneyDesk = ({ sessionId, detail, onChange, setErr }) => {
  const pricing = detail?.pricing || {};
  const xero = detail?.xero || {};
  const [tier, setTier] = useState(pricing.tier || '');
  const [fee, setFee] = useState(pricing.setup_fee_aud != null ? String(pricing.setup_fee_aud) : '');
  const [email, setEmail] = useState(pricing.contact_email || detail?.delivery?.email?.to || '');
  const [addons, setAddons] = useState(() => {
    const saved = pricing.addons || [];
    const suggested = detail?.suggested_addons || [];
    const merged = [...saved];
    suggested.forEach((s) => {
      if (!merged.some((m) => m.id === s.id)) {
        merged.push({ id: s.id, label: s.label || s.id, price_aud: ADDON_ANCHORS[s.id] ?? 0, include: false });
      }
    });
    return merged;
  });
  const [busy, setBusy] = useState('');

  const act = async (label, fn) => {
    setBusy(label); setErr('');
    try { await fn(); await onChange(); }
    catch (e) { setErr(e?.response?.data?.detail || `${label} failed.`); }
    setBusy('');
  };
  const pickTier = (t) => { setTier(t.id); setFee(String(t.anchor)); };
  const setAddon = (i, patch) => setAddons(addons.map((a, x) => (x === i ? { ...a, ...patch } : a)));
  const savePricing = () => act('save', () =>
    axios.post(`${API}/admin/workshop/sessions/${sessionId}/pricing`,
      { tier, setup_fee_aud: parseFloat(fee || '0'), addons, contact_email: email }, auth()));
  const createInvoice = () => {
    if (!window.confirm('Create a DRAFT invoice in Xero for this build?\n\nDraft only — Michael reviews and sends it from Xero. Kindra never sends or takes payment.')) return;
    act('invoice', () =>
      axios.post(`${API}/admin/workshop/sessions/${sessionId}/xero_invoice`, {}, auth()));
  };
  const refresh = () => act('refresh', () =>
    axios.post(`${API}/admin/workshop/sessions/${sessionId}/xero_refresh`, {}, auth()));

  const included = addons.filter((a) => a.include && parseFloat(a.price_aud || 0) > 0);
  const total = (parseFloat(fee || '0') || 0) + included.reduce((s, a) => s + (parseFloat(a.price_aud) || 0), 0);
  const priceSaved = pricing.setup_fee_aud > 0;
  const inputS = { background: C.surface, border: `1px solid ${C.hair}`, borderRadius: 99, color: C.icy, fontFamily: 'Inter' };
  const chip = (active) => ({
    background: active ? 'rgba(240,195,109,0.14)' : 'transparent',
    color: active ? '#F0C36D' : C.muted,
    border: `1px solid ${active ? 'rgba(240,195,109,0.45)' : C.hair}`,
    fontFamily: 'Poppins', cursor: 'pointer',
  });
  const payColor = { paid: '#7FE8CE', partial: '#F0C36D', unpaid: C.muted }[xero.payment_status] || C.muted;

  return (
    <div className="mt-4 rounded-xl p-4" data-testid="workshop-money-desk"
         style={{ background: C.navy, border: '1px solid rgba(240,195,109,0.3)' }}>
      <p className="text-[13px] font-bold mb-1" style={{ color: '#F0C36D', fontFamily: 'Poppins' }}>
        Money desk <span style={{ color: C.faint, fontWeight: 500, fontFamily: 'Inter' }}>· Xero is the source of truth — Kindra only creates the DRAFT, you send it from Xero</span>
      </p>

      {!xero.invoice_id && (
        <>
          <div className="flex flex-wrap gap-2 mt-2 mb-2">
            {TIERS.map((t) => (
              <button key={t.id} data-testid={`workshop-tier-${t.id}`} onClick={() => pickTier(t)}
                      className="px-4 py-2 rounded-full text-[12.5px] font-bold" style={chip(tier === t.id)}>
                {t.label} · ${t.anchor.toLocaleString()}{t.id === 'complex' ? '+' : ''}
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <label className="text-[12.5px]" style={{ color: C.muted, fontFamily: 'Inter' }}>Setup fee (AUD, you set the final number)</label>
            <input data-testid="workshop-fee-input" type="number" min="0" value={fee}
                   onChange={(e) => setFee(e.target.value)}
                   className="w-[120px] px-4 py-2 text-[13px] outline-none" style={inputS} />
            <input data-testid="workshop-billing-email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="billing contact email (for the Xero contact)"
                   className="flex-1 min-w-[220px] px-4 py-2 text-[13px] outline-none" style={inputS} />
          </div>
          {addons.length > 0 && (
            <div className="mb-2" data-testid="workshop-addon-pricing">
              <p className="text-[11.5px] mb-1" style={{ color: C.faint, fontFamily: 'Inter' }}>
                Add-ons captured in the chat — tick to include as line items (anchors prefilled, you set final):
              </p>
              {addons.map((a, i) => (
                <div key={a.id || i} className="flex items-center gap-2 mb-1">
                  <label className="flex items-center gap-2 text-[12.5px] flex-1" style={{ color: a.include ? C.icy : C.muted, fontFamily: 'Inter', cursor: 'pointer' }}>
                    <input type="checkbox" data-testid={`workshop-addon-include-${a.id}`} checked={!!a.include}
                           onChange={(e) => setAddon(i, { include: e.target.checked })} />
                    {a.label || a.id}
                  </label>
                  <span className="text-[12px]" style={{ color: C.faint }}>$</span>
                  <input data-testid={`workshop-addon-price-${a.id}`} type="number" min="0" value={a.price_aud}
                         onChange={(e) => setAddon(i, { price_aud: e.target.value })}
                         className="w-[90px] px-3 py-1.5 text-[12.5px] outline-none" style={inputS} />
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[12.5px] font-bold" data-testid="workshop-price-total"
                  style={{ color: C.icy, fontFamily: 'Poppins' }}>
              Invoice total: ${total.toLocaleString()} AUD
            </span>
            <button data-testid="workshop-save-pricing" onClick={savePricing}
                    disabled={busy === 'save' || !tier || !(parseFloat(fee || '0') > 0)}
                    className="px-4 py-2 rounded-full text-[12.5px] font-bold"
                    style={{ background: 'rgba(240,195,109,0.14)', color: '#F0C36D',
                             border: '1px solid rgba(240,195,109,0.45)', fontFamily: 'Poppins', cursor: 'pointer',
                             opacity: tier && parseFloat(fee || '0') > 0 ? 1 : 0.45 }}>
              {busy === 'save' ? 'Saving…' : priceSaved ? '✓ Update pricing' : 'Save pricing'}
            </button>
            <button data-testid="workshop-xero-create" onClick={createInvoice}
                    disabled={busy === 'invoice' || !priceSaved}
                    title={priceSaved ? 'Creates a DRAFT in Xero — you send it from Xero' : 'Save pricing first'}
                    className="px-4 py-2 rounded-full text-[12.5px] font-bold"
                    style={{ background: priceSaved ? C.spectrum : 'rgba(220,240,245,0.08)',
                             color: priceSaved ? '#fff' : C.faint, border: 'none',
                             fontFamily: 'Poppins', cursor: priceSaved ? 'pointer' : 'default' }}>
              {busy === 'invoice' ? 'Creating draft…' : 'Create Xero DRAFT invoice'}
            </button>
          </div>
        </>
      )}

      {xero.invoice_id && (
        <div className="flex flex-wrap items-center gap-2 mt-2" data-testid="workshop-xero-status">
          <span className="text-[12.5px] px-3 py-1.5 rounded-full font-bold"
                style={{ color: '#7FE8CE', background: 'rgba(52,209,176,0.12)',
                         border: '1px solid rgba(52,209,176,0.4)', fontFamily: 'Inter' }}
                data-testid="workshop-xero-number">
            Xero {xero.invoice_status || 'DRAFT'} · {xero.invoice_number}
          </span>
          <span className="text-[12.5px]" style={{ color: payColor, fontFamily: 'Inter' }} data-testid="workshop-xero-payment">
            {xero.payment_status === 'paid' ? '✓ Paid' : xero.payment_status === 'partial'
              ? `Part-paid $${(xero.amount_paid || 0).toLocaleString()}` : 'Unpaid'}
            {xero.total ? ` · total $${Number(xero.total).toLocaleString()} AUD` : ''}
          </span>
          <button data-testid="workshop-xero-refresh" onClick={refresh} disabled={busy === 'refresh'}
                  className="px-3 py-1.5 rounded-full text-[12px]"
                  style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.hair}`,
                           fontFamily: 'Inter', cursor: 'pointer' }}>
            {busy === 'refresh' ? 'Checking…' : '↻ Refresh payment status'}
          </button>
          <span className="text-[11.5px]" style={{ color: C.faint, fontFamily: 'Inter' }}>
            Draft only — send it from Xero.
          </span>
        </div>
      )}
      {xero.error && (
        <p className="text-[12px] mt-2" style={{ color: '#FF7DB3', fontFamily: 'Inter' }} data-testid="workshop-xero-error">
          Xero error: {xero.error} — fix and retry with "Create Xero DRAFT invoice".
        </p>
      )}
    </div>
  );
};

const DeliveryDesk = ({ sessionId, token, detail, build, onChange, setErr }) => {
  const delivery = detail?.delivery || {};
  const addons = detail?.suggested_addons || [];
  const [includeAddons, setIncludeAddons] = useState(true);
  const [toEmail, setToEmail] = useState('');
  const [notes, setNotes] = useState(delivery.notes || '');
  const [busy, setBusy] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [copied, setCopied] = useState(false);

  const act = async (label, fn) => {
    if (busy) return;
    setBusy(label); setErr('');
    try { await fn(); await onChange(); }
    catch (e) { setErr(e?.response?.data?.detail || `${label} failed.`); }
    setBusy('');
  };
  const setMode = (mode) => act('mode', () =>
    axios.post(`${API}/admin/workshop/sessions/${sessionId}/delivery/mode`, { mode }, auth()));
  const genGuide = () => {
    if (!window.confirm('Generate the branded install guide? Uses the quality model.')) return;
    act('guide', () => axios.post(`${API}/admin/workshop/sessions/${sessionId}/guide`,
      { confirm: true, include_addons: includeAddons }, auth()));
  };
  const sendEmail = () => {
    if (!window.confirm(`Email the plugin${delivery.guide ? ' + guide' : ''} to ${toEmail}?`)) return;
    act('email', () => axios.post(`${API}/admin/workshop/sessions/${sessionId}/deliver/email`,
      { confirm: true, to_email: toEmail }, auth()));
  };
  const markDelivered = () => {
    if (!window.confirm('Mark this session as delivered?')) return;
    act('mark', () => axios.post(`${API}/admin/workshop/sessions/${sessionId}/deliver/mark`,
      { confirm: true, via: 'manual' }, auth()));
  };
  const toggleItem = (id, done) => act('check', () =>
    axios.post(`${API}/admin/workshop/sessions/${sessionId}/delivery/checklist`, { item_id: id, done }, auth()));
  const saveNotes = () => act('notes', () =>
    axios.post(`${API}/admin/workshop/sessions/${sessionId}/delivery/checklist`, { notes }, auth()));
  const copyLink = async () => {
    const url = `${API}/workshop/${token}/plugin.zip`;
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch {}
  };

  const chip = (active) => ({
    background: active ? 'rgba(52,209,176,0.14)' : 'transparent',
    color: active ? '#7FE8CE' : C.muted,
    border: `1px solid ${active ? 'rgba(52,209,176,0.4)' : C.hair}`,
    fontFamily: 'Poppins', cursor: 'pointer',
  });

  return (
    <div className="mt-4 rounded-xl p-4" data-testid="workshop-delivery-desk"
         style={{ background: C.navy, border: `1px solid ${C.hair}` }}>
      <p className="text-[13px] font-bold mb-1" style={{ color: C.icy, fontFamily: 'Poppins' }}>
        Delivery {delivery.state === 'delivered' &&
          <span style={{ color: '#7FE8CE' }}>— delivered {delivery.delivered_via === 'email' ? `by email to ${delivery.email?.to}` : '(manual)'}</span>}
      </p>
      <div className="flex flex-wrap gap-2 mb-3">
        <button data-testid="workshop-mode-handover" onClick={() => setMode('handover')}
                className="px-4 py-2 rounded-full text-[13px] font-bold" style={chip(delivery.mode === 'handover')}>
          Hand over the zip
        </button>
        <button data-testid="workshop-mode-managed" onClick={() => setMode('kindra_managed')}
                className="px-4 py-2 rounded-full text-[13px] font-bold" style={chip(delivery.mode === 'kindra_managed')}>
          Kindra installs & runs it
        </button>
      </div>

      {addons.length > 0 && (
        <p className="text-[12.5px] mb-3 px-3 py-2 rounded-lg" data-testid="workshop-delivery-addons"
           style={{ color: '#F0C36D', background: 'rgba(240,195,109,0.07)',
                    border: '1px solid rgba(240,195,109,0.3)', fontFamily: 'Inter' }}>
          💰 Recommend these paid add-ons to the client: {addons.map((a) => a.label).join(' · ')}
        </p>
      )}

      {delivery.mode === 'handover' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[12.5px] flex items-center gap-2" style={{ color: C.muted, fontFamily: 'Inter' }}>
              <input type="checkbox" data-testid="workshop-guide-addons-toggle" checked={includeAddons}
                     onChange={(e) => setIncludeAddons(e.target.checked)} />
              include "things we can also set up" (add-ons) in the guide
            </label>
            <button data-testid="workshop-guide-btn" onClick={genGuide} disabled={busy === 'guide'}
                    className="px-4 py-2 rounded-full text-[13px] font-bold"
                    style={{ background: 'rgba(79,143,208,0.14)', color: '#B8CDE8',
                             border: '1px solid rgba(79,143,208,0.4)', fontFamily: 'Poppins', cursor: 'pointer' }}>
              {busy === 'guide' ? 'Writing guide…' : delivery.guide ? '↻ Regenerate install guide' : 'Generate install guide'}
            </button>
            {delivery.guide && (
              <button data-testid="workshop-guide-view" onClick={() => setShowGuide((v) => !v)}
                      className="px-3 py-2 rounded-full text-[12.5px]"
                      style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.hair}`,
                               fontFamily: 'Inter', cursor: 'pointer' }}>
                {showGuide ? 'Hide guide' : 'View guide'}
              </button>
            )}
          </div>
          {showGuide && delivery.guide && (
            <pre className="text-[11.5px] p-4 rounded-xl whitespace-pre-wrap" data-testid="workshop-guide-content"
                 style={{ background: C.surface, border: `1px solid ${C.hair}`, color: C.muted,
                          fontFamily: 'ui-monospace, monospace', maxHeight: 340, overflowY: 'auto' }}>
              {delivery.guide.content_md}
            </pre>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input data-testid="workshop-deliver-email-input" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                   placeholder="client@business.com.au"
                   className="flex-1 min-w-[200px] px-4 py-2 text-[13px] outline-none"
                   style={{ background: C.surface, border: `1px solid ${C.hair}`, borderRadius: 99,
                            color: C.icy, fontFamily: 'Inter' }} />
            <button data-testid="workshop-deliver-email-btn" onClick={sendEmail}
                    disabled={busy === 'email' || !toEmail.includes('@')}
                    className="px-4 py-2 rounded-full text-[13px] font-bold"
                    style={{ background: C.spectrum, color: '#fff', border: 'none',
                             fontFamily: 'Poppins', cursor: 'pointer',
                             opacity: toEmail.includes('@') ? 1 : 0.5 }}>
              {busy === 'email' ? 'Sending…' : 'Email zip + guide to client'}
            </button>
            <button data-testid="workshop-copy-dl-link" onClick={copyLink}
                    className="px-3 py-2 rounded-full text-[12.5px]"
                    style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.hair}`,
                             fontFamily: 'Inter', cursor: 'pointer' }}>
              {copied ? '✓ Copied' : 'Copy client download link'}
            </button>
            {delivery.state !== 'delivered' && (
              <button data-testid="workshop-mark-delivered" onClick={markDelivered}
                      className="px-3 py-2 rounded-full text-[12.5px]"
                      style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.hair}`,
                               fontFamily: 'Inter', cursor: 'pointer' }}>
                Mark delivered (sent it myself)
              </button>
            )}
          </div>
        </div>
      )}

      {delivery.mode === 'kindra_managed' && (
        <div data-testid="workshop-managed-checklist">
          <p className="text-[12.5px] mb-2" style={{ color: C.muted, fontFamily: 'Inter' }}>
            Kindra-managed — no client zip. Your install checklist:
          </p>
          {(delivery.checklist || []).map((item) => (
            <label key={item.id} className="flex items-center gap-2 text-[13px] mb-1.5"
                   style={{ color: item.done ? '#7FE8CE' : C.icy, fontFamily: 'Inter', cursor: 'pointer' }}>
              <input type="checkbox" checked={item.done} data-testid={`workshop-check-${item.id}`}
                     onChange={(e) => toggleItem(item.id, e.target.checked)} />
              {item.label}
            </label>
          ))}
          <textarea data-testid="workshop-managed-notes" value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={2} placeholder="Notes (access details to collect, quirks, promises made…)"
                    className="w-full px-3 py-2 mt-2 text-[13px] outline-none resize-none"
                    style={{ background: C.surface, border: `1px solid ${C.hair}`, borderRadius: 12,
                             color: C.icy, fontFamily: 'Inter' }} />
          <div className="flex gap-2 mt-2">
            <button data-testid="workshop-save-notes" onClick={saveNotes}
                    className="px-4 py-2 rounded-full text-[12.5px]"
                    style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.hair}`,
                             fontFamily: 'Inter', cursor: 'pointer' }}>
              Save notes
            </button>
            {delivery.state !== 'delivered' && (
              <button data-testid="workshop-managed-done" onClick={markDelivered}
                      className="px-4 py-2 rounded-full text-[12.5px] font-bold"
                      style={{ background: 'rgba(52,209,176,0.14)', color: '#7FE8CE',
                               border: '1px solid rgba(52,209,176,0.4)', fontFamily: 'Poppins', cursor: 'pointer' }}>
                ✓ Installed & handed over
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const STAGE_STYLE = {
  draft:     { label: 'Draft',     color: '#B8CDE8', bg: 'rgba(79,143,208,0.12)' },
  ready:     { label: 'Ready',     color: '#B8CDE8', bg: 'rgba(79,143,208,0.12)' },
  generated: { label: 'Generated — awaiting review', color: '#F0C36D', bg: 'rgba(240,195,109,0.10)' },
  approved:  { label: 'Approved',  color: '#7FE8CE', bg: 'rgba(52,209,176,0.12)' },
  delivered: { label: 'Delivered', color: '#7FE8CE', bg: 'rgba(52,209,176,0.18)' },
};

export const StagePill = ({ stage }) => {
  const s = STAGE_STYLE[stage] || STAGE_STYLE.draft;
  return (
    <span data-testid={`workshop-stage-${stage}`} className="text-[11px] px-2.5 py-1 rounded-full font-semibold"
          style={{ fontFamily: 'Inter', color: s.color, background: s.bg, border: `1px solid ${s.color}44` }}>
      {s.label}
    </span>
  );
};

export const V2WorkshopBuildPanel = ({ sessionId, sessionReady, token }) => {
  const [build, setBuild] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [openFile, setOpenFile] = useState(null);
  const [detail, setDetail] = useState(null);
  const [regenNote, setRegenNote] = useState('');
  const pollRef = useRef(null);

  const loadDetail = async () => {
    try {
      const r = await axios.get(`${API}/admin/workshop/sessions/${sessionId}`, auth());
      setDetail(r.data);
    } catch {}
  };
  useEffect(() => { loadDetail(); }, [sessionId]);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/workshop/sessions/${sessionId}/build`, auth());
      setBuild(r.data);
      if (r.data?.status !== 'generating' && pollRef.current) {
        clearInterval(pollRef.current); pollRef.current = null;
      }
      if (r.data?.status === 'generating' && !pollRef.current) {
        pollRef.current = setInterval(load, 3500);
      }
    } catch { setErr('Could not load build state.'); }
  };
  useEffect(() => { load(); return () => pollRef.current && clearInterval(pollRef.current); }, [sessionId]);

  const generate = async () => {
    if (busy) return;
    if (!window.confirm('Generate this client\'s plugin now? Uses the quality model (Michael-gated).')) return;
    setBusy(true); setErr('');
    try {
      await axios.post(`${API}/admin/workshop/sessions/${sessionId}/generate`, { confirm: true }, auth());
      setBuild({ status: 'generating' });
      pollRef.current = setInterval(load, 3500);
    } catch (e) { setErr(e?.response?.data?.detail || 'Generation failed to start.'); }
    setBusy(false);
  };

  const download = async () => {
    try {
      const r = await axios.get(`${API}/admin/workshop/sessions/${sessionId}/build/download`,
        { ...auth(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = build.filename || 'plugin.zip';
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { setErr('Download failed.'); }
  };

  const status = build?.status || 'none';
  const validation = build?.validation;
  const stage = detail?.stage || 'draft';
  const delivery = detail?.delivery || {};

  const approve = async () => {
    try {
      await axios.post(`${API}/admin/workshop/sessions/${sessionId}/review`, { action: 'approve' }, auth());
      await loadDetail();
    } catch (e) { setErr(e?.response?.data?.detail || 'Approve failed.'); }
  };
  const regenerate = async () => {
    if (!window.confirm('Regenerate the plugin with your note? Uses the quality model.')) return;
    try {
      await axios.post(`${API}/admin/workshop/sessions/${sessionId}/review`,
        { action: 'regenerate', confirm: true, note: regenNote }, auth());
      setRegenNote(''); setBuild({ status: 'generating' });
      pollRef.current = setInterval(load, 3500);
      await loadDetail();
    } catch (e) { setErr(e?.response?.data?.detail || 'Regenerate failed.'); }
  };

  return (
    <div className="rounded-2xl p-5 mt-6" data-testid="workshop-build-panel"
         style={{ background: C.surface, border: `1px solid ${C.hair}` }}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-[13px] font-bold uppercase tracking-[0.14em]" style={{ color: C.muted, fontFamily: 'Poppins' }}>
            Plugin build <span style={{ color: C.faint, textTransform: 'none', letterSpacing: 0 }}>· Phase 2</span>
          </p>
          <p className="text-[12.5px] mt-0.5" style={{ color: C.faint, fontFamily: 'Inter' }}>
            {status === 'none' && (sessionReady
              ? 'Session is ready — generate the installable Cowork plugin for this client.'
              : 'Finish the onboarding chat first — generation unlocks when the session is ready.')}
            {status === 'generating' && 'Writing the plugin with the quality model… ~1-2 minutes.'}
            {status === 'ready' && `Validated build ready · ${build.plugin_name} · generation cost ~$${(build.cost_cents / 100).toFixed(2)} USD`}
            {status === 'failed_validation' && 'Build generated but FAILED validation — download blocked.'}
            {status === 'failed' && `Generation failed (${build?.error || 'error'}) — try again.`}
          </p>
        </div>
        {status === 'generating' ? (
          <span data-testid="workshop-build-generating" className="text-[13px] px-4 py-2 rounded-full font-bold"
                style={{ color: C.icy, border: `1px solid ${C.hair}`, fontFamily: 'Poppins' }}>
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 99, background: C.spectrum,
                           marginRight: 8, animation: 'workshop-dot 1.2s infinite ease-in-out' }} />
            Generating…
          </span>
        ) : (
          <div className="flex gap-2">
            <button data-testid="workshop-generate-btn" onClick={generate}
                    disabled={busy || !sessionReady || status === 'generating'}
                    className="px-5 py-2 rounded-full text-[13.5px] font-bold"
                    style={{ background: sessionReady ? C.spectrum : 'rgba(220,240,245,0.08)',
                             color: sessionReady ? '#fff' : C.faint, border: 'none',
                             fontFamily: 'Poppins', cursor: sessionReady ? 'pointer' : 'default' }}>
              {status === 'ready' || status === 'failed_validation' ? 'Regenerate' : 'Generate plugin'}
            </button>
            {status === 'ready' && validation?.pass && (
              <button data-testid="workshop-download-btn" onClick={download}
                      className="px-5 py-2 rounded-full text-[13.5px] font-bold"
                      style={{ background: 'rgba(52,209,176,0.14)', color: '#7FE8CE',
                               border: '1px solid rgba(52,209,176,0.4)', fontFamily: 'Poppins', cursor: 'pointer' }}>
                ↓ {build.filename}
              </button>
            )}
          </div>
        )}
      </div>
      {err && <p className="text-[13px] mt-2" style={{ color: '#FF7DB3', fontFamily: 'Inter' }}>{err}</p>}

      {validation && (
        <div className="mt-4 rounded-xl p-4" data-testid="workshop-validation-report"
             style={{ background: C.navy, border: `1px solid ${validation.pass ? 'rgba(52,209,176,0.35)' : 'rgba(235,37,141,0.4)'}` }}>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <p className="text-[13px] font-bold" style={{ color: validation.pass ? '#7FE8CE' : '#FF7DB3', fontFamily: 'Poppins' }}>
              Validation: {validation.pass ? 'PASS' : 'FAIL'}
            </p>
            <StagePill stage={stage} />
          </div>
          {validation.checks.map((c) => (
            <p key={c.id} className="text-[12.5px] mb-1" style={{ color: c.pass ? C.muted : '#FF7DB3', fontFamily: 'Inter' }}>
              {c.pass ? '✓' : '✗'} {c.label}{c.detail ? <span style={{ color: C.faint }}> — {c.detail}</span> : null}
            </p>
          ))}
        </div>
      )}

      {/* Review gate */}
      {status === 'ready' && validation?.pass && stage === 'generated' && (
        <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="workshop-review-actions">
          <button data-testid="workshop-approve-btn" onClick={approve}
                  className="px-5 py-2 rounded-full text-[13.5px] font-bold"
                  style={{ background: 'rgba(52,209,176,0.14)', color: '#7FE8CE',
                           border: '1px solid rgba(52,209,176,0.4)', fontFamily: 'Poppins', cursor: 'pointer' }}>
            ✓ Approve — client-ready
          </button>
          <input data-testid="workshop-regen-note" value={regenNote} onChange={(e) => setRegenNote(e.target.value)}
                 placeholder="Optional note, e.g. make the FAQ skill warmer"
                 className="flex-1 min-w-[220px] px-4 py-2 text-[13px] outline-none"
                 style={{ background: C.navy, border: `1px solid ${C.hair}`, borderRadius: 99, color: C.icy, fontFamily: 'Inter' }} />
          <button data-testid="workshop-regen-btn" onClick={regenerate}
                  className="px-4 py-2 rounded-full text-[13px] font-semibold"
                  style={{ background: 'transparent', color: C.muted, border: `1px solid ${C.hair}`,
                           fontFamily: 'Inter', cursor: 'pointer' }}>
            ↻ Regenerate with note
          </button>
        </div>
      )}

      {/* Money desk + Delivery desk */}
      {(stage === 'approved' || stage === 'delivered') && (
        <>
          <MoneyDesk sessionId={sessionId} detail={detail} onChange={loadDetail} setErr={setErr} />
          <DeliveryDesk sessionId={sessionId} token={token} detail={detail}
                        build={build} onChange={loadDetail} setErr={setErr} />
        </>
      )}

      {build?.files && (
        <div className="mt-4" data-testid="workshop-file-preview">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] mb-2" style={{ color: C.faint, fontFamily: 'Inter' }}>
            What's inside · {build.files.length} files
          </p>
          {build.files.map((f) => (
            <div key={f.path}>
              <button onClick={() => setOpenFile(openFile === f.path ? null : f.path)}
                      data-testid={`workshop-file-${f.path.replace(/[^a-z0-9]+/gi, '-')}`}
                      className="w-full text-left px-3 py-2 rounded-lg mb-1 flex items-baseline gap-2"
                      style={{ background: openFile === f.path ? 'rgba(79,143,208,0.10)' : 'transparent',
                               border: `1px solid ${openFile === f.path ? 'rgba(79,143,208,0.35)' : 'transparent'}`,
                               cursor: 'pointer' }}>
                <span className="text-[13px] font-semibold" style={{ color: C.icy, fontFamily: 'Inter' }}>{f.path}</span>
                <span className="text-[11.5px]" style={{ color: C.faint, fontFamily: 'Inter' }}>— {f.desc} · {(f.size / 1024).toFixed(1)}kb</span>
              </button>
              {openFile === f.path && (
                <pre className="text-[11.5px] p-4 rounded-xl mb-2 overflow-x-auto whitespace-pre-wrap"
                     style={{ background: C.navy, border: `1px solid ${C.hair}`, color: C.muted,
                              fontFamily: 'ui-monospace, monospace', maxHeight: 380, overflowY: 'auto' }}>
                  {f.content}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default V2WorkshopBuildPanel;
